/**
 * Authorize.net getSettledBatchList rejects ranges longer than ~31 days.
 * Fetch settled batches in chunks and merge.
 */

export const PAYMENT_API_BASE = 'https://vue-payment-server.fly.dev';

/** Authorize.net max settlement date window (inclusive days). Stay under 31. */
const MAX_RANGE_DAYS = 30;

function toDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function parseDateOnly(yyyyMmDd) {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

function buildChunkRanges(startDate, endDate) {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (start > end) return [];

  const ranges = [];
  let cursor = start;
  while (cursor <= end) {
    const chunkEnd = addDays(cursor, MAX_RANGE_DAYS - 1);
    const capped = chunkEnd > end ? end : chunkEnd;
    ranges.push({
      startDate: formatDateOnly(cursor),
      endDate: formatDateOnly(capped),
    });
    cursor = addDays(capped, 1);
  }
  return ranges;
}

async function fetchBatchChunk(startDate, endDate, apiBase = PAYMENT_API_BASE) {
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetch(`${apiBase}/api/batches?${params}`);
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.message || data.error || 'Failed to retrieve batch list');
  }
  return Array.isArray(data.batches) ? data.batches : [];
}

/**
 * Fetch settled batches for an inclusive date range, chunking for Authorize.net limits.
 * @param {{ startDate: string|Date, endDate?: string|Date, apiBase?: string }} opts
 * @returns {Promise<{ success: boolean, batches: array, message?: string, dateRange: { startDate: string, endDate: string } }>}
 */
export async function fetchSettledBatches({ startDate, endDate, apiBase = PAYMENT_API_BASE } = {}) {
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate || new Date());
  if (!start || !end) {
    return { success: false, batches: [], message: 'Invalid date range', dateRange: { startDate: start, endDate: end } };
  }

  const ranges = buildChunkRanges(start, end);
  const byId = new Map();
  const errors = [];

  const results = await Promise.all(
    ranges.map(async (range) => {
      try {
        const batches = await fetchBatchChunk(range.startDate, range.endDate, apiBase);
        return { ok: true, batches };
      } catch (err) {
        return { ok: false, error: `${range.startDate}→${range.endDate}: ${err.message || err}` };
      }
    })
  );

  results.forEach((result) => {
    if (!result.ok) {
      errors.push(result.error);
      return;
    }
    result.batches.forEach((b) => {
      const id = String(b.batchId);
      if (!byId.has(id)) byId.set(id, b);
    });
  });

  const batches = Array.from(byId.values()).sort((a, b) => {
    const ta = a.settlementTimeUTC || a.settlementTimeLocal || '';
    const tb = b.settlementTimeUTC || b.settlementTimeLocal || '';
    return ta.localeCompare(tb);
  });

  if (!batches.length && errors.length) {
    return {
      success: false,
      batches: [],
      message: errors[0],
      dateRange: { startDate: start, endDate: end },
    };
  }

  return {
    success: true,
    batches,
    message: errors.length ? `Retrieved with partial errors (${errors.length} chunks failed)` : undefined,
    dateRange: { startDate: start, endDate: end },
  };
}

/**
 * Aggregate batch statistics by YYYY-MM settlement month.
 */
export function aggregateBatchesByMonth(batches = []) {
  const map = {};
  batches.forEach((b) => {
    const ts = b.settlementTimeUTC || b.settlementTimeLocal || '';
    if (!ts || ts.length < 7) return;
    const key = ts.slice(0, 7);
    if (!map[key]) {
      map[key] = {
        chargeAmount: 0,
        refundAmount: 0,
        chargeCount: 0,
        refundCount: 0,
        voidCount: 0,
        declineCount: 0,
        errorCount: 0,
      };
    }
    const stats = b.statistics || {};
    map[key].chargeAmount += stats.chargeAmount || 0;
    map[key].refundAmount += stats.refundAmount || 0;
    map[key].chargeCount += stats.chargeCount || 0;
    map[key].refundCount += stats.refundCount || 0;
    map[key].voidCount += stats.voidCount || 0;
    map[key].declineCount += stats.declineCount || 0;
    map[key].errorCount += stats.errorCount || 0;
  });
  return map;
}
